import React, { useState, useEffect } from 'react';
import { Bio } from './types';
import {
  BioEditorContainer,
  BioEditorHeader,
  BioEditorTitle,
  BioEditorDescription,
  BioEditorContent,
  BioTextarea,
  BioCounter,
  BioSuggestions,
  SuggestionItem,
  BioTips,
  TipItem,
  CharacterCount,
  WordCount,
  ValidationMessage,
  BioPreview
} from '../../styles/Profile/ProfileEditor.styles';

interface BioEditorProps {
  bio: Bio;
  onChange: (bio: Bio) => void;
}

const MAX_CHARACTERS = 160;
const MIN_CHARACTERS = 20;
const MAX_WORDS = 30;

const BIO_SUGGESTIONS = [
  "I love exploring new places and trying different cuisines",
  "Passionate about fitness and outdoor adventures",
  "Looking for someone to share life's little moments with",
  "I enjoy reading, hiking, and good conversation",
  "Seeking a genuine connection with someone special",
  "Love traveling, photography, and making memories",
  "I'm a foodie who loves cooking and trying new restaurants",
  "Looking for someone who shares my love for music and art",
  "I enjoy staying active and trying new sports",
  "Seeking someone to laugh with and create adventures together"
];

const BIO_TIPS = [
  "Be authentic and genuine in your description",
  "Include your interests and hobbies",
  "Mention what you're looking for in a relationship",
  "Keep it positive and upbeat",
  "Avoid clichés and generic statements",
  "Show your personality through your words",
  "Be specific about your interests",
  "Include what makes you unique",
  "Update your bio regularly",
  "Ask friends for feedback on your bio"
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const BioEditor: React.FC<BioEditorProps> = ({
  bio,
  onChange
}) => {
  const [content, setContent] = useState(bio.content);
  const [wordCount, setWordCount] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const validateBio = (content: string): string[] => {
    const errors: string[] = [];

    if (content.length < MIN_CHARACTERS) {
      errors.push(`Bio must be at least ${MIN_CHARACTERS} characters long`);
    }

    if (content.length > MAX_CHARACTERS) {
      errors.push(`Bio must be no more than ${MAX_CHARACTERS} characters`);
    }

    const words = countWords(content);
    if (words > MAX_WORDS) {
      errors.push(`Bio must be no more than ${MAX_WORDS} words`);
    }

    if (words < 5) {
      errors.push('Bio should be at least 5 words');
    }

    return errors;
  };

  const handleBioChange = (newContent: string) => {
    // Enforce hard cap of 160 characters
    const truncatedContent = newContent.slice(0, MAX_CHARACTERS);
    const characterCount = truncatedContent.length;
    const wordCount = countWords(truncatedContent);
    const errors = validateBio(truncatedContent);

    const updatedBio: Bio = {
      ...bio,
      content: truncatedContent,
      characterCount,
      lastUpdated: new Date()
    };

    setContent(truncatedContent);
    setWordCount(wordCount);
    setValidationErrors(errors);

    onChange(updatedBio);
  };

  const handleSuggestionClick = (suggestion: string) => {
    const newContent = content + (content ? ' ' : '') + suggestion;
    handleBioChange(newContent);
    setShowSuggestions(false);
  };

  const getCharacterCountColor = (): string => {
    const percentage = (bio.characterCount / MAX_CHARACTERS) * 100;
    if (percentage >= 90) return '#ff4757';
    if (percentage >= 75) return '#ffa502';
    return '#2ed573';
  };

  const getWordCountColor = (): string => {
    if (wordCount > MAX_WORDS) return '#ff4757';
    if (wordCount < 5) return '#ffa502';
    return '#2ed573';
  };

  return (
    <BioEditorContainer>
      <BioEditorHeader>
        <BioEditorTitle>Edit Your Bio</BioEditorTitle>
        <BioEditorDescription>
          Tell others about yourself! A good bio helps people get to know you better and increases your chances of making meaningful connections.
        </BioEditorDescription>
      </BioEditorHeader>

      <BioEditorContent>
        <BioTextarea
          value={content}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleBioChange(e.target.value)}
          placeholder="Tell us about yourself... What are your interests? What are you looking for? What makes you unique?"
          rows={8}
          maxLength={MAX_CHARACTERS}
        />

        <BioCounter>
          <CharacterCount color={getCharacterCountColor()}>
            {bio.characterCount}/{MAX_CHARACTERS} characters
          </CharacterCount>
          <WordCount color={getWordCountColor()}>
            {wordCount} words
          </WordCount>
        </BioCounter>
      </BioEditorContent>

      {showSuggestions && (
        <BioSuggestions>
          <h4>Need inspiration? Try these:</h4>
          {BIO_SUGGESTIONS.map((suggestion, index) => (
            <SuggestionItem
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </SuggestionItem>
          ))}
        </BioSuggestions>
      )}

      {bio.content.length < MIN_CHARACTERS && (
        <button
          onClick={() => setShowSuggestions(!showSuggestions)}
          style={{
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '1rem'
          }}
        >
          {showSuggestions ? 'Hide Suggestions' : 'Show Suggestions'}
        </button>
      )}

      {validationErrors.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          {validationErrors.map((error, index) => (
            <ValidationMessage key={index} $type="error">{error}</ValidationMessage>
          ))}
        </div>
      )}

      {bio.content.length >= MIN_CHARACTERS && validationErrors.length === 0 && (
        <BioPreview>
          <h4>Preview:</h4>
          <p>{bio.content}</p>
        </BioPreview>
      )}

      <BioTips>
        <h4>Bio Writing Tips:</h4>
        {BIO_TIPS.map((tip, index) => (
          <TipItem key={index}>{tip}</TipItem>
        ))}
      </BioTips>
    </BioEditorContainer>
  );
};
